import React from "react";

const SURFACE_IMAGE_URL =
  "https://customer-assets.emergentagent.com/job_tiszta-tukor/artifacts/2pq71sjc_ChatGPT%20Image%202026.%20j%C3%BAn.%2024.%2021_49_09.png";

export default function BeneathSurfaceSection() {
  return (
    <section
      id="felszin-alatt"
      data-testid="beneath-surface-section"
      className="relative py-10 md:py-14 px-6 md:px-10"
    >
      <div className="max-w-5xl mx-auto">
        {/* Eyebrow */}
        <p
          data-testid="beneath-surface-eyebrow"
          className="text-[12px] tracking-[0.35em] uppercase text-gold mb-5 font-body text-center"
        >
          — A felszín alatt
        </p>

        {/* Title */}
        <h2
          data-testid="beneath-surface-title"
          className="font-serif-display text-[34px] sm:text-[40px] md:text-[46px] leading-[1.15] text-graphite mb-10 text-center"
        >
          Ami a felszín{" "}
          <em className="text-gold" style={{ fontStyle: "italic" }}>
            alatt
          </em>{" "}
          van
        </h2>

        {/* Symbolic image — what is above and what is beneath */}
        <figure className="my-8 md:my-10">
          <div className="portrait-frame relative max-w-xl mx-auto">
            <img
              data-testid="beneath-surface-image"
              src={SURFACE_IMAGE_URL}
              alt="Egy alak, akinek gyökerei mélyen a víz alatt nyúlnak — a felszín alatti világ"
              className="w-full h-auto object-cover block"
              style={{
                aspectRatio: "1 / 1",
                filter: "saturate(0.96) contrast(1.02)",
              }}
              loading="lazy"
            />
          </div>
        </figure>

        {/* Body — flowing book prose with strategic gold accents */}
        <div className="reading-prose mt-10 text-[16px] md:text-[17px] leading-[1.9] text-graphite font-body font-light">
          <p>
            A saját kérdéseim vezettek erre az útra. Nem egy konkrét módszer vagy könyv. Sokkal
            inkább azok az élethelyzetek, amelyekre hosszú ideig nem találtam valódi választ.
            Félelmek, szorongások, megmagyarázhatatlan érzetek és visszatérő minták, amelyek újra
            és újra arra hívtak, hogy mélyebbre nézzek annál, mint amit a felszínen láttam. Az út
            során számtalan eszközzel, szemlélettel és módszerrel találkoztam. Némelyiket csak
            kipróbáltam, másokat tanultam és hivatás szerűen is alkalmaztam. Sok mindent
            megértettem általuk, mégis azt éreztem, hogy valami hiányzik.
          </p>

          <div className="my-6 md:my-8 text-center">
            <p className="font-serif-display italic text-[24px] sm:text-[28px] md:text-[32px] leading-[1.35] text-gold max-w-xl mx-auto">
              Nem a magyarázat.{" "}
              <span className="text-graphite">A valódi változás.</span>
            </p>
          </div>

          <p>
            A legtöbb válasz megmutatta, mi történik velem és a kliensekkel. Megmutatta a
            mintákat, az összefüggéseket, és gyakran azt is, hogy miért működünk úgy,
            ahogy. Mégis azt láttam, hogy a felismerés önmagában nem mindig hoz valódi
            átalakulást. Sokszor csak egy újabb történetet ad arról, hogy kik vagyunk.
            Egy újabb meghatározást. Egy újabb címkét. Egy újabb magyarázatot. És bár
            ezek gyakran megkönnyebbülést hoznak, könnyen az önigazolás csapdájába eshet
            az ember.
          </p>

          {/* Inset panel — the three self-justifying stories */}
          <aside
            data-testid="beneath-surface-selfquotes"
            className="my-8 md:my-10 mx-auto max-w-md bg-warm border border-hairline px-7 md:px-10 py-7 md:py-8"
          >
            <ul className="list-none space-y-3 md:space-y-4 text-center">
              {[
                '„Ilyen vagyok."',
                '„Nekem ez a sorsom."',
                '„Nálam ez ezért működik így."',
              ].map((s, idx, arr) => (
                <li
                  key={s}
                  className="font-serif-display italic text-graphite text-[17px] md:text-[18.5px] leading-[1.55]"
                >
                  {s}
                  {idx < arr.length - 1 && (
                    <span
                      aria-hidden="true"
                      className="block mx-auto mt-3 md:mt-4 w-3 h-px bg-[var(--accent-gold)] opacity-50"
                    />
                  )}
                </li>
              ))}
            </ul>
          </aside>

          <p>
            Engem azonban nem az érdekelt, hogyan tudjuk pontosabban leírni a jelenlegi
            valóságunkat. Hanem az, hogyan lehet túllépni rajta. Hogyan lehet meglátni
            azokat a hiedelmeket, láthatatlan azonosulásokat és rögzült történeteket,
            amelyek észrevétlenül alakítják a döntéseinket, a kapcsolatainkat és végső
            soron az egész életünket.
          </p>

          <div className="my-6 md:my-8 text-center">
            <p className="font-serif-display italic text-[22px] sm:text-[26px] md:text-[30px] leading-[1.4] text-graphite max-w-xl mx-auto">
              Mert egy idő után már nem az a kérdés, hogy
              <br />
              <span className="text-gold">miért vagyunk olyanok, amilyenek.</span>
            </p>
            <p className="font-serif-display italic text-[22px] sm:text-[26px] md:text-[30px] leading-[1.4] text-graphite max-w-xl mx-auto mt-6">
              Hanem az, hogy{" "}
              <span className="text-gold">valóban azok vagyunk-e.</span>
            </p>
          </div>

          <p>
            A konzultációk során ezért nem kész válaszokat szeretnék adni. Sokkal inkább
            egy olyan teret teremteni, ahol lehetőség nyílik meglátni azt, ami addig
            rejtve maradt. Ahol megkérdőjelezhetővé válnak azok a történetek, amelyeket
            évek vagy akár évtizedek óta valóságnak hiszünk. Mert sokszor nem az
            történik, hogy találunk egy új választ. Hanem megreped valami.
          </p>

          {/* Cascade — premium inset panel matching the other intro boxes */}
          <aside
            data-testid="beneath-surface-cascade"
            className="my-8 md:my-10 mx-auto max-w-md bg-warm border border-hairline px-7 md:px-10 py-7 md:py-8"
          >
            <ul className="list-none space-y-3 md:space-y-4 text-center">
              {[
                "Egy történet.",
                "Egy önazonosság.",
                "Egy szerep.",
                'Egy „én ilyen vagyok" mondat.',
              ].map((s, idx, arr) => (
                <li
                  key={s}
                  className="font-serif-display italic text-graphite text-[17px] md:text-[18.5px] leading-[1.55]"
                >
                  {s}
                  {idx < arr.length - 1 && (
                    <span
                      aria-hidden="true"
                      className="block mx-auto mt-3 md:mt-4 w-3 h-px bg-[var(--accent-gold)] opacity-50"
                    />
                  )}
                </li>
              ))}
            </ul>
          </aside>

          {/* Closing emphasis line */}
          <div className="my-6 md:my-8 text-center">
            <p className="font-serif-display italic text-gold text-[24px] sm:text-[28px] md:text-[32px] leading-[1.35] max-w-xl mx-auto">
              És ott kezdődik a szabadság.
            </p>
          </div>

          <p>
            Azt szeretném, hogy akik hozzám fordulnak, ne az én válaszaimat vigyék haza
            magukkal. Hanem megtalálják a sajátjaikat. Azokat, amelyek talán mindig is
            ott voltak bennük, csak eddig nem volt elég csend ahhoz, hogy meghallják
            őket.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto mt-10 md:mt-12 hairline" />
    </section>
  );
}
